import React, { Component } from "react";
import { Container } from "@sainsburys-tech/fable";
import { Routes, Route, Navigate, BrowserRouter, useParams } from "react-router-dom";
import PropTypes from "prop-types";

import applicationInsights from "../../utils/application-insights";

import * as DefaultRoutes from "../../utils/defaultRoutes";
import EnabledFeaturesContext from "../../context/enabledFeaturesContext";
import UserRolesContext from "../../context/userRolesContext";
import { ToastProvider } from "../../context/toastContext";
import { OverrideToastProvider } from "../../context/overrideToastContext";
import { ManagersOverrideToastProvider } from "../../context/managersOverrideToastContext";
import { BannerProvider } from "../../context/bannerContext";
import * as roles from "../../utils/roles";
import WhenAuthorized from "../WhenAuthorized";
import StoresContext from "../../context/storesContext";

import BackToTopButton from "../BackToTopButton";
import NavigationBar from "../NavigationBar/NavigationBar.js";
import NotFoundView from "../NotFoundView";
import AccessDeniedView from "../AccessDeniedView";

import AlertsPage from "../AlertsPage";
import AlertDetailsPage from "../AlertDetailsPage";
import DeviceDetailsPage from "../DeviceDetailsPage";
import StoreDetailsPage from "../StoreDetailsPage/";
import StoresPage from "../StoresPage/";
import StoreZoneSceneLevelsPage from "../StoreZoneSceneLevelsPage/";
import StoreZoneConfigurePage from "../StoreZoneConfigurePage/";
import StoreCommissioningPage from "../StoreCommissioningPage/";
import StoreOverridePage from "../StoreOverridePage/";
import OverridesPage from "../OverridesPage";
import OverridesGroupedListPage from "../OverridesGroupedListPage";
import OverrideEdit from "../OverrideEdit";
import LightingStatusPage from "../LightingStatusPage";
import ScrollToTop from "../ScrollToTop";
import { PageView, initGA } from "../Tracking";

import "./App.scss";

applicationInsights.initialise();

// React Router v6: protected route wrappers
const LightingSetupRoute = ({ component: RouteComponent }) => (
  <WhenAuthorized roles={[roles.LIGHTING_SETUP]} fallback={<AccessDeniedView />}>
    <RouteComponent />
  </WhenAuthorized>
);

const LightingOverridesReadRoute = ({ component: RouteComponent }) => (
  <WhenAuthorized roles={[roles.LIGHTING_OVERRIDES_READ]} fallback={<AccessDeniedView />}>
    <RouteComponent />
  </WhenAuthorized>
);

const LightingOverridesWriteRoute = ({ component: RouteComponent }) => (
  <WhenAuthorized
    roles={[roles.LIGHTING_OVERRIDES_WRITE, roles.LIGHTING_OVERRIDES_READ]}
    fallback={<AccessDeniedView />}
  >
    <RouteComponent />
  </WhenAuthorized>
);

const AdminRoute = ({ component: RouteComponent }) => (
  <WhenAuthorized roles={[roles.ADMIN]} fallback={<AccessDeniedView />}>
    <RouteComponent />
  </WhenAuthorized>
);

// Wrapper to inject storeId param for StoreDetailsPage key remount
const StoreDetailsPageWrapper = () => {
  const { storeId } = useParams();
  return <StoreDetailsPage key={storeId} storeId={storeId} />;
};

// Wrapper to inject deviceId param for DeviceDetailsPage
const DeviceDetailsPageParamsWrapper = () => {
  const { deviceId } = useParams();
  return <DeviceDetailsPage deviceId={deviceId} />;
};

// Redirect for /lighting/stores/:storeId/commissioning → .../zones
const StoreCommissioningRedirect = () => {
  const { storeId } = useParams();
  return <Navigate to={`/lighting/stores/${storeId}/commissioning/zones`} replace />;
};

class App extends Component {
  componentDidMount() {
    initGA("UA-77515350-20");
    PageView();
  }

  render() {
    return (
      <EnabledFeaturesContext.Provider value={this.props.enabledFeatures}>
        <UserRolesContext.Provider value={this.props.userRoles}>
          <StoresContext.Provider
            value={{
              stores: this.props.stores,
              onLightingStoresChange: this.props.onLightingStoresChange
            }}
          >
            <BrowserRouter>
              <ScrollToTop>
                <NavigationBar>
                  <BannerProvider>
                    <ToastProvider>
                      <OverrideToastProvider>
                        <ManagersOverrideToastProvider>
                          <Container style={{ paddingLeft: '2rem', paddingRight: '2rem', marginTop: '-2rem' }}>
                            <Routes>
                              <Route
                                path="/"
                                element={<Navigate to={DefaultRoutes.DEFAULT_ALERT_ROUTE} replace />}
                              />
                              <Route
                                path="/lighting/stores/:storeId/zones/:zoneId/scene-levels"
                                element={<LightingSetupRoute component={StoreZoneSceneLevelsPage} />}
                              />
                              <Route
                                path="/lighting/stores/:storeId/zones/add"
                                element={<LightingSetupRoute component={StoreZoneConfigurePage} />}
                              />
                              <Route
                                path="/lighting/overrides/add"
                                element={<LightingOverridesWriteRoute component={StoreOverridePage} />}
                              />
                              <Route
                                path="/lighting/overrides/:overrideId/edit"
                                element={<LightingOverridesWriteRoute component={OverrideEdit} />}
                              />
                              <Route
                                path="/lighting/overrides/:overrideId"
                                element={<LightingOverridesWriteRoute component={OverridesGroupedListPage} />}
                              />
                              <Route
                                path="/lighting/stores/:storeId/zones/:zoneId/configuration"
                                element={<LightingSetupRoute component={StoreZoneConfigurePage} />}
                              />
                              <Route
                                path="/stores/:storeId"
                                element={<StoreDetailsPageWrapper />}
                              />
                              <Route
                                path="/lighting/stores/:storeId/commissioning"
                                element={<StoreCommissioningRedirect />}
                              />
                              <Route
                                path="/lighting/stores/:storeId/commissioning/*"
                                element={<LightingSetupRoute component={StoreCommissioningPage} />}
                              />
                              <Route path="/stores" element={<StoresPage />} />
                              <Route path="/alerts" element={<AlertsPage />} />
                              <Route
                                path="/alerts/:alertId"
                                element={<AlertDetailsPage />}
                              />
                              <Route
                                path="/devices/:deviceId"
                                element={<DeviceDetailsPageParamsWrapper />}
                              />
                              <Route
                                path="/lighting/overrides"
                                element={<LightingOverridesReadRoute component={OverridesPage} />}
                              />
                              <Route
                                path="/lighting/status"
                                element={<AdminRoute component={LightingStatusPage} />}
                              />
                              <Route path="*" element={<NotFoundView />} />
                            </Routes>
                          </Container>
                        </ManagersOverrideToastProvider>
                      </OverrideToastProvider>
                    </ToastProvider>
                  </BannerProvider>
                </NavigationBar>
              </ScrollToTop>
            </BrowserRouter>
            <BackToTopButton />
          </StoresContext.Provider>
        </UserRolesContext.Provider>
      </EnabledFeaturesContext.Provider>
    );
  }
}

App.propTypes = {
  enabledFeatures: PropTypes.array
};

export default App;
